import React from 'react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import { css } from '@emotion/css';
import { TaskType } from 'utils/TaskAPI';

import { colors } from 'styles/colors';
import { timelineIcons } from 'atoms/TaskCard';

import Return from 'assets/icons/return.svg';

type TaskModalProps = {
    task: TaskType | null;
    openModal: boolean;
    handleCloseModal: () => void;
};

const TaskModal = ({ task, openModal, handleCloseModal }: TaskModalProps) => {
    return (
        <Modal
            open={openModal}
            onClose={handleCloseModal}
            disableAutoFocus
            aria-labelledby="task-modal-title"
            aria-describedby="task-modal-description"
            className={styles.modalBase}
        >
            <Box sx={styles.modal}>
                <button
                    type="button"
                    className={styles.returnArrow}
                    onClick={handleCloseModal}
                    aria-label="Luk"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="31" height="19" aria-hidden>
                        <image href={Return} width="31" height="19" />
                    </svg>
                </button>
                <div className={styles.centerContent}>
                    <div aria-hidden>
                        <svg xmlns="http://www.w3.org/2000/svg" width={70} height={70}>
                            {task && <image href={timelineIcons[task.iconType]} width={70} height={70} />}
                        </svg>
                    </div>
                    <h2 id="task-modal-title" className={styles.title}>
                        {task?.title}
                    </h2>
                    <div className={styles.date}>
                        {task && new Date(task.dueOnDate).toLocaleString('da-DK', { day: 'numeric', month: 'short' })}
                    </div>
                    <div id="task-modal-description" className={styles.text}>
                        <div dangerouslySetInnerHTML={{ __html: task ? task.description : '' }} />
                    </div>
                </div>
            </Box>
        </Modal>
    );
};

const styles = {
    modalBase: css({
        '.MuiModal-backdrop': {
            marginTop: '0rem',
        },
    }),
    modal: {
        position: 'absolute' as 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 'min(90vw, 38rem)',
        maxWidth: '38rem',
        maxHeight: '80vh',
        height: 'auto',
        minHeight: '60vh',
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 3,
        overflow: 'hidden',
        outline: 'none',
        '&:focus': { outline: 'none' },
        '&:focus-visible': { outline: 'none' },
        borderRadius: '12px',
    },
    title: css({
        marginTop: '1rem',
        marginBottom: '0.5rem',
        color: colors.primaryText,
        textAlign: 'center',
        fontFamily: 'Figtree',
        fontSize: '30px',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: '130%',
    }),
    date: css({
        marginBottom: '1.5rem',
        color: colors.primaryText,
        fontFamily: 'Figtree',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: '140%',
    }),
    text: css({
        maxWidth: '34rem',
        width: '100%',
        maxHeight: '50vh',
        overflowY: 'auto',
        fontFamily: 'Figtree',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '150%',
        paddingRight: '0.5rem',
        paddingBottom: '1.25rem',
        // Custom scrollbar styles (WebKit-based browsers)
        '::-webkit-scrollbar': {
            width: '8px',
        },
        '::-webkit-scrollbar-track': {
            background: 'transparent',
            borderRadius: '8px',
        },
        '::-webkit-scrollbar-thumb': {
            backgroundColor: '#cfcfcf',
            borderRadius: '8px',
            border: '2px solid transparent',
            backgroundClip: 'content-box',
        },
        '::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#bdbdbd',
        },
        // Firefox scrollbar styles
        scrollbarWidth: 'thin',
        scrollbarColor: '#cfcfcf transparent',
    }),
    centerContent: css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: '1rem',
    }),
    returnArrow: css({
        position: 'absolute',
        width: '1.9375rem',
        height: '1.1875rem',
        flexShrink: 0,
        cursor: 'pointer',
        top: '1.5rem',
        left: '1.5rem',
        border: 'none',
        background: 'transparent',
        padding: 0,
        outline: 'none',
    }),
};

export default TaskModal;
